import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { APP } from "@/constants/testIds";
import { Download, FileText } from "lucide-react";

const KIND_LABEL = {
  business_plan: "Business Plan",
  marketing_plan: "Marketing Plan",
  auditor: "Business Audit",
  industry: "Industry Analysis",
  pitch_outline: "Pitch Outline",
  financials: "Financials",
  capital_readiness: "Capital Readiness",
  health_report: "Health Report",
};

export default function Assets() {
  const [assets, setAssets] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => { (async () => {
    const { data } = await api.get("/assets");
    setAssets(data.assets || []);
    if (data.assets?.length) setSelected(data.assets[0]);
  })(); }, []);

  const download = async (id, fmt) => {
    try {
      const res = await api.get(`/assets/${id}/download`, { params: { fmt }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `asset.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="label-mono mb-2">Business Assets Library</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">Every document. Always current.</h1>
        <p className="mt-2 text-sm text-stone-600">All AI-generated assets in one place. Export to Word & PDF anytime.</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <div className="rounded-lg border border-stone-200 bg-white">
          <div className="border-b border-stone-200 p-3">
            <div className="label-mono">Library ({assets.length})</div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-stone-100">
            {assets.length === 0 && <div className="p-6 text-xs text-stone-500">No assets yet. Generate a document from any AI module.</div>}
            {assets.map((a) => (
              <button key={a.id} onClick={() => setSelected(a)} className={`block w-full p-4 text-left transition-colors ${selected?.id === a.id ? "bg-stone-100" : "hover:bg-stone-50"}`}>
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-slate-950 flex-shrink-0" strokeWidth={1.5} />
                  <div className="text-sm font-medium text-slate-950 truncate">{a.title}</div>
                </div>
                <div className="mt-1 pl-6 font-mono text-[10px] uppercase tracking-wider text-stone-500">
                  {KIND_LABEL[a.kind] || a.kind} • {new Date(a.created_at).toLocaleDateString()}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="card-flat">
          {selected ? (
            <>
              <div className="flex items-start justify-between border-b border-stone-200 pb-4">
                <div>
                  <div className="label-mono">{KIND_LABEL[selected.kind] || selected.kind}</div>
                  <h2 className="mt-2 font-display text-2xl font-medium text-slate-950">{selected.title}</h2>
                  <div className="mt-1 font-mono text-xs text-stone-500">{new Date(selected.created_at).toLocaleString()}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => download(selected.id, "docx")} className="btn-secondary !text-xs !px-3 !py-2">
                    <Download className="h-3.5 w-3.5" /> Word
                  </button>
                  <button onClick={() => download(selected.id, "pdf")} className="btn-secondary !text-xs !px-3 !py-2">
                    <Download className="h-3.5 w-3.5" /> PDF
                  </button>
                </div>
              </div>
              <div className="mt-6 whitespace-pre-wrap text-sm leading-relaxed text-slate-900">
                {selected.content}
              </div>
            </>
          ) : (
            <div className="flex h-64 items-center justify-center text-sm text-stone-500">Select an asset to preview.</div>
          )}
        </div>
      </div>
    </div>
  );
}
